import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function SolutionModules() {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const modules = [
    {
      icon: 'ri-robot-2-line',
      title: t('solution.module1.title'),
      description: t('solution.module1.desc'),
      features: [t('solution.module1.feature1'), t('solution.module1.feature2'), t('solution.module1.feature3'), t('solution.module1.feature4')],
      color: 'cyan' as const,
      delay: '0s'
    },
    {
      icon: 'ri-star-smile-line',
      title: t('solution.module2.title'),
      description: t('solution.module2.desc'),
      features: [t('solution.module2.feature1'), t('solution.module2.feature2'), t('solution.module2.feature3'), t('solution.module2.feature4')],
      color: 'orange' as const,
      delay: '0.2s'
    },
    {
      icon: 'ri-share-line',
      title: t('solution.module3.title'),
      description: t('solution.module3.desc'),
      features: [t('solution.module3.feature1'), t('solution.module3.feature2'), t('solution.module3.feature3'), t('solution.module3.feature4')],
      color: 'cyan' as const,
      delay: '0.4s'
    },
    {
      icon: 'ri-google-line',
      title: t('solution.module4.title'),
      description: t('solution.module4.desc'),
      features: [t('solution.module4.feature1'), t('solution.module4.feature2'), t('solution.module4.feature3'), t('solution.module4.feature4')],
      color: 'orange' as const,
      delay: '0.6s'
    }
  ];

  const themeClasses = {
    cyan: {
      borderHover: 'hover:border-blue-300',
      bgIcon: 'bg-gradient-to-br from-[#10B981] to-[#1E3A8A]',
      borderIcon: 'border-transparent',
      shadowIcon: 'shadow-md shadow-blue-900/10',
      textIcon: 'text-white',
      textTitleHover: 'group-hover:text-[#10B981]',
      textCheck: 'text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A]',
      gradient: 'from-[#10B981] to-transparent'
    },
    orange: {
      borderHover: 'hover:border-blue-300',
      bgIcon: 'bg-gradient-to-br from-[#10B981] to-[#1E3A8A]',
      borderIcon: 'border-transparent',
      shadowIcon: 'shadow-md shadow-blue-900/10',
      textIcon: 'text-white',
      textTitleHover: 'group-hover:text-[#10B981]',
      textCheck: 'text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A]',
      gradient: 'from-[#1E3A8A] to-transparent'
    }
  };

  return (
    <section ref={sectionRef} id="funktionen" className="py-12 sm:py-16 md:py-24 lg:py-32 bg-slate-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-1/4 left-0 w-48 h-48 md:w-72 md:h-72 bg-blue-200/20 rounded-full blur-[100px]"></div>
      <div className="absolute bottom-1/4 right-0 w-48 h-48 md:w-72 md:h-72 bg-emerald-200/20 rounded-full blur-[100px]"></div>

      {/* Grid Overlay */}
      <div className="absolute inset-0 bg-[url('/images/grid.svg')] bg-center opacity-[0.03]"></div>

      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-8 sm:mb-12 md:mb-16 lg:mb-20 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-white/50 backdrop-blur-sm rounded-full border border-blue-200 mb-3 sm:mb-4 md:mb-6 shadow-sm">
            <i className="ri-lightbulb-line text-[#10B981] text-xs sm:text-sm md:text-base"></i>
            <span className="text-xs sm:text-sm font-medium text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A]" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              {t('solution.badge')}
            </span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] mb-3 sm:mb-4 md:mb-6 px-2" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
            <span className="bg-gradient-to-r from-[#10B981] to-[#1E3A8A] bg-clip-text text-transparent animate-pulse">
              {t('solution.title')} {t('solution.titleHighlight')}
            </span>
          </h2>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-slate-600 max-w-3xl mx-auto px-2" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            {t('solution.subtitle')}
          </p>
        </div>

        {/* Modules Grid */}
        <div className="grid md:grid-cols-2 gap-5 sm:gap-6 md:gap-8">
          {modules.map((module, index) => {
            const theme = themeClasses[module.color];
            return (
              <div
                key={index}
                className={`group bg-white rounded-xl sm:rounded-2xl md:rounded-3xl p-5 sm:p-6 md:p-8 border border-slate-200 hover:bg-slate-50 transition-all duration-500 shadow-sm hover:shadow-lg ${theme.borderHover} ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                  }`}
                style={{
                  transitionDelay: isVisible ? module.delay : '0s'
                }}
              >
                {/* Icon */}
                <div className={`w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-5 md:mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 border ${theme.bgIcon} ${theme.borderIcon} ${theme.shadowIcon}`}>
                  <i className={`${module.icon} text-xl sm:text-2xl md:text-3xl ${theme.textIcon}`}></i>
                </div>

                {/* Content */}
                <h3 className={`text-base sm:text-lg md:text-xl font-bold text-[#0F172A] mb-2 sm:mb-3 md:mb-4 transition-colors ${theme.textTitleHover}`} style={{ fontFamily: 'Inter Tight, sans-serif' }}>
                  {module.title}
                </h3>
                <p className="text-xs sm:text-sm md:text-base text-slate-600 leading-relaxed mb-4 sm:mb-5 md:mb-6" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                  {module.description}
                </p>

                {/* Features List */}
                <div className="space-y-2 sm:space-y-2.5 md:space-y-3">
                  {module.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 sm:gap-2.5 md:gap-3">
                      <div className="w-5 h-5 md:w-6 md:h-6 bg-white rounded-full flex items-center justify-center flex-shrink-0 border border-slate-200">
                        <i className={`ri-check-line text-xs sm:text-sm ${theme.textCheck}`}></i>
                      </div>
                      <span className="text-xs sm:text-sm text-slate-600 font-medium group-hover:text-[#0F172A] transition-colors" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Hover Effect */}
                <div className={`mt-5 sm:mt-6 md:mt-8 h-px bg-gradient-to-r ${theme.gradient} scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`}></div>
              </div>
            );
          })}
        </div>

        {/* Bottom Stats */}
        <div className={`grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-8 mt-8 sm:mt-12 md:mt-16 lg:mt-20 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {[
            { value: '95%', label: t('solution.stat1') },
            { value: '3x', label: t('solution.stat2') },
            { value: '+40%', label: t('solution.stat3') },
            { value: '24/7', label: t('solution.stat4') }
          ].map((stat, index) => (
            <div key={index} className="group text-center p-3 sm:p-4 md:p-6 bg-white rounded-xl sm:rounded-2xl border border-slate-200 shadow-sm hover:border-emerald-200 hover:bg-slate-50 hover:shadow-lg transition-all duration-300">
              <div
                className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#10B981] group-hover:text-[#0D9468] mb-1 sm:mb-2 transition-colors duration-300"
                style={{ fontFamily: 'Inter Tight, sans-serif' }}
                dangerouslySetInnerHTML={{ __html: stat.value }}
              ></div>
              <p
                className="text-xs sm:text-sm text-slate-500 group-hover:text-slate-700 font-medium leading-tight transition-colors duration-300"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
